import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { User, Mail, Phone, MapPin, Bell, Globe, Lock } from 'lucide-react';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import api from '@/lib/axios';
import { PlzOrtBridge } from '@/components/address/PlzOrtBridge';
import { getPortalToken, getPortalUser } from './portalStorage';

export default function PortalSettings() {
  const [user, setUser] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [profileForm, setProfileForm] = useState({
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    email_notifications: true,
    language: 'de',
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = getPortalToken();
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await api.get('/portal/me');
      setUser(response.data);
      
      // Get guardian details if needed
      const userData = getPortalUser() || {};
      setGuardian(userData);
      
      setProfileForm({
        phone: response.data.phone || '',
        address: response.data.address || '',
        city: response.data.city || '',
        postal_code: response.data.postal_code || '',
        email_notifications: response.data.email_notifications,
        language: response.data.language || 'de',
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Fehler beim Laden des Profils');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/portal/me', profileForm);
      toast.success('Profil gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/portal/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Passwort geändert');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="space-y-6 max-w-2xl" data-testid="portal-settings">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Einstellungen</h1>
          <p className="text-slate-600">Verwalten Sie Ihr Profil und Ihre Einstellungen</p>
        </div>

        {/* Profile Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profil
            </CardTitle>
            <CardDescription>Ihre persönlichen Daten</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={user?.guardian_name || ''} disabled className="bg-slate-50" />
              </div>
              <div>
                <Label>E-Mail</Label>
                <Input value={user?.email || ''} disabled className="bg-slate-50" />
              </div>
            </div>
            
            <div>
              <Label htmlFor="phone">Telefon</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="phone"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                  className="pl-10"
                  placeholder="Telefonnummer"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="address">Adresse</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="address"
                  value={profileForm.address}
                  onChange={(e) => setProfileForm({...profileForm, address: e.target.value})}
                  className="pl-10"
                  placeholder="Straße und Hausnummer"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="postal_code">PLZ</Label>
                <Input
                  id="postal_code"
                  value={profileForm.postal_code}
                  onChange={(e) => setProfileForm({...profileForm, postal_code: e.target.value})}
                  placeholder="PLZ"
                />
              </div>
              <div>
                <Label htmlFor="city">Ort</Label>
                <Input
                  id="city"
                  value={profileForm.city}
                  onChange={(e) => setProfileForm({...profileForm, city: e.target.value})}
                  placeholder="Ort"
                />
                <PlzOrtBridge
                  postalCode={profileForm.postal_code}
                  city={profileForm.city}
                  onCity={(value) => setProfileForm((prev) => ({ ...prev, city: value }))}
                  testId="portal-plz"
                />
              </div>
            </div>
            
            <Button onClick={saveProfile} disabled={saving}>
              {saving ? 'Speichern...' : 'Änderungen speichern'}
            </Button>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Benachrichtigungen
            </CardTitle>
            <CardDescription>E-Mail-Hinweise bei neuen Nachrichten und wichtigen Ereignissen. DSGVO-konform: nur Hinweis-Link, kein Inhalt in der E-Mail.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>E-Mail-Hinweise bei neuen Nachrichten</Label>
                <p className="text-sm text-slate-500">
                  Erhalten Sie eine kurze E-Mail, sobald Ihnen jemand in der App schreibt.<br/>
                  Maximal 1 Hinweis alle 5 Minuten pro Konversation — kein Spam.
                </p>
              </div>
              <Switch
                checked={profileForm.email_notifications}
                data-testid="portal-settings-email-notifications"
                onCheckedChange={(checked) => {
                  setProfileForm({...profileForm, email_notifications: checked});
                  // Auto-save
                  api.put('/portal/me', {...profileForm, email_notifications: checked})
                    .then(() => toast.success('Einstellung gespeichert'))
                    .catch(() => toast.error('Fehler beim Speichern'));
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Passwort ändern
            </CardTitle>
            <CardDescription>Ändern Sie Ihr Anmeldepasswort</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="current_password">Aktuelles Passwort</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
              />
            </div>
            
            <div>
              <Label htmlFor="new_password">Neues Passwort</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                placeholder="Mindestens 8 Zeichen"
              />
            </div>
            
            <div>
              <Label htmlFor="confirm_password">Passwort bestätigen</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
              />
            </div>
            
            <Button 
              onClick={changePassword} 
              disabled={saving || !passwordForm.current_password || !passwordForm.new_password}
              variant="outline"
            >
              {saving ? 'Ändern...' : 'Passwort ändern'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
